import { useState } from "react";
import {
  ArrowRight,
  Eye,
  EyeSlash,
  LockKey,
  ShieldCheck,
} from "@phosphor-icons/react";
import { Link, useNavigate } from "react-router-dom";

import { useAuth } from "@/context/AuthContext";
import BrandLogo from "@/components/BrandLogo";
import { formatApiError } from "@/lib/api";
import { rolePaths } from "@/lib/roles";

const audienceCopy = {
  learner: {
    eyebrow: "Learner access",
    heading: "Continue your learning",
    intro: "Sign in to access your assigned pathways, assessments, and certificates.",
    action: "Enter learner workspace",
  },
  trainer: {
    eyebrow: "Trainer access",
    heading: "Manage learning delivery",
    intro: "Sign in to monitor your cohorts, course outcomes, and certificates.",
    action: "Enter trainer workspace",
  },
  admin: {
    eyebrow: "Administrator access",
    heading: "Run training operations",
    intro: "Sign in to manage courses, learning assignments, and people.",
    action: "Enter administrator workspace",
  },
};

export default function LoginPage({ audience = "learner" }) {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const copy = audienceCopy[audience];

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);
    try {
      const user = await login({ email, password });
      navigate(rolePaths[user.role]);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="auth-screen" data-testid="login-page">
      <section className="auth-visual" data-testid="login-visual-panel">
        <div className="auth-grid" aria-hidden="true" />
        <div className="auth-visual-content">
          <BrandLogo testId="login-brand-logo" variant="auth" />
          <p className="eyebrow">Skillexpress Learning Solutions Pvt. Ltd.</p>
          <h1>Capability, made measurable.</h1>
          <p>One focused place for you to learn, apply, and prove new skills.</p>
          <div className="auth-stat-line" data-testid="login-platform-benefit">
            <ShieldCheck aria-hidden="true" size={26} weight="duotone" />
            <span>Secure role-based learning workspace</span>
          </div>
        </div>
      </section>
      <section className="auth-form-panel">
        <div className="auth-form-wrap">
          <BrandLogo testId="login-form-brand-logo" variant="form" />
          <p className="eyebrow" data-testid="login-audience-label">{copy.eyebrow}</p>
          <h2 data-testid="login-heading">{copy.heading}</h2>
          <p className="auth-intro">{copy.intro}</p>
          <form className="auth-form" onSubmit={submit} data-testid="login-form">
            <label htmlFor="email">Email address</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@company.com"
              autoComplete="email"
              required
              data-testid="login-email-input"
            />
            <label htmlFor="password">Password</label>
            <div className="password-field">
              <LockKey aria-hidden="true" size={20} />
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
                data-testid="login-password-input"
              />
              <button
                className="password-visibility-button"
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                data-testid="toggle-password-visibility-button"
              >
                {showPassword ? (
                  <EyeSlash aria-hidden="true" size={20} />
                ) : (
                  <Eye aria-hidden="true" size={20} />
                )}
              </button>
            </div>
            <Link className="forgot-password-link" to="/forgot-password" data-testid="forgot-password-link">
              Forgot password?
            </Link>
            {error && <p className="form-error" role="alert" data-testid="login-error-message">{error}</p>}
            <button className="primary-button" disabled={isSubmitting} data-testid="login-submit-button">
              {isSubmitting ? "Signing in" : copy.action}
              <ArrowRight aria-hidden="true" size={19} weight="bold" />
            </button>
          </form>
          <nav className="role-access-links" aria-label="Choose your sign-in area">
            <p data-testid="alternate-access-label">Need a different workspace?</p>
            <Link to="/login" data-testid="learner-login-link">Learner sign in</Link>
            <Link to="/login/trainer" data-testid="trainer-login-link">Trainer sign in</Link>
            <Link to="/login/admin" data-testid="admin-login-link">Administrator sign in</Link>
          </nav>
        </div>
      </section>
    </main>
  );
}